import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { DatePicker } from '../components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DryHireInvoicingPanel } from '../components/schedule/DryHireInvoicingPanel';
import {
  RefreshCw, ChevronDown, ChevronRight, AlertTriangle, Download,
  DollarSign, Package, Clock, FileText, Receipt, ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';

// Money — uses tenant currency from auth user, falling back to AUD.
const fmt$ = (n, currency = 'AUD') => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);
};

// Hire-status pill colour mapping. Mirrors `hire_status` states from the
// backend state machine (services/hire_lifecycle.py).
const STATUS_COLOURS = {
  booked: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  dispatched: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  on_hire: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  off_hired: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

export default function DryHireInvoicingDashboard() {
  const { api, user } = useAuth();
  const currency = user?.tenant?.currency || 'AUD';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Filters — server-side
  const [asOf, setAsOf] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [statusFilter, setStatusFilter] = useState('open');
  const [customerSearch, setCustomerSearch] = useState('');
  const [minWip, setMinWip] = useState('0');

  // Drill-down sheet
  const [drillDown, setDrillDown] = useState(null); // {jobId, jobNumber, customerName, assetLabel}

  // Expanded customer rows
  const [expanded, setExpanded] = useState({});

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        as_of: asOf,
        status: statusFilter,
        min_wip: String(parseFloat(minWip) || 0),
      });
      const res = await api.get(`/dry-hires/invoicing-dashboard?${params}`);
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load invoicing dashboard';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, asOf, statusFilter, minWip]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData(true);
  };

  // Local-only filter — typing the customer name filters the already-fetched
  // rollup. Avoids round-tripping for a UI affordance.
  const visibleCustomers = useMemo(() => {
    if (!data?.customers) return [];
    const q = customerSearch.trim().toLowerCase();
    if (!q) return data.customers;
    return data.customers.filter(c => (c.customer_name || '').toLowerCase().includes(q));
  }, [data, customerSearch]);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const expandAll = () => {
    const all = {};
    visibleCustomers.forEach(c => { all[c.customer_id || '__none__'] = true; });
    setExpanded(all);
  };
  const collapseAll = () => setExpanded({});

  const exportCsv = () => {
    if (!data?.customers?.length) {
      toast.warning('Nothing to export');
      return;
    }
    const rows = [
      ['Customer', 'Job#', 'Asset', 'Site', 'Started', 'Last Invoiced To',
       'Days On Hire', 'Days Uninvoiced', 'Hire Status',
       'WIP Full Cycles', 'WIP Blend', 'Billed To Date', 'Overdue'],
    ];
    visibleCustomers.forEach(c => {
      c.hires.forEach(h => rows.push([
        c.customer_name || '',
        h.job_number ?? '',
        h.asset_label || '',
        h.site_label || '',
        h.start_date || '',
        h.last_invoiced_to || '',
        h.days_on_hire ?? '',
        h.days_uninvoiced ?? '',
        h.hire_status,
        h.wip_full_cycles,
        h.wip_blend,
        h.billed_to_date,
        h.is_overdue ? 'YES' : '',
      ]));
    });
    const csv = rows.map(r => r.map(v => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dry-hire-wip-${asOf}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const k = data?.kpis || { open_hires: 0, wip_total: 0, billed_to_date: 0, overdue_hires: 0, awaiting_final: 0 };

  return (
    <div className="container mx-auto px-4 py-6 space-y-6" data-testid="dryhire-invoicing-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="dashboard-title">
            Dry-Hire Invoicing
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Portfolio-wide view of every open dry-hire ledger and the dollars sitting in WIP.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={loading || !data?.customers?.length} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" />Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading || refreshing} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kpi-strip">
        <KpiCard testId="kpi-open-hires" icon={Package} label="Open Hires" value={k.open_hires} />
        <KpiCard testId="kpi-wip-total" icon={DollarSign} label="WIP $ (uninvoiced)" value={fmt$(k.wip_total, currency)} accent="text-emerald-600" />
        <KpiCard testId="kpi-billed-to-date" icon={Receipt} label="Billed-to-Date" value={fmt$(k.billed_to_date, currency)} />
        <KpiCard testId="kpi-overdue" icon={AlertTriangle} label="Overdue Hires" value={k.overdue_hires} accent={k.overdue_hires > 0 ? 'text-red-600' : ''} />
        <KpiCard testId="kpi-awaiting-final" icon={Clock} label="Awaiting Final Invoice" value={k.awaiting_final} accent={k.awaiting_final > 0 ? 'text-amber-600' : ''} />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">As Of</Label>
              <DatePicker
                value={asOf}
                onChange={(d) => setAsOf(d || format(new Date(), 'yyyy-MM-dd'))}
                className="w-full mt-1"
                testId="filter-as-of"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="mt-1" data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open (booked → off-hired)</SelectItem>
                  <SelectItem value="overdue_only">Overdue only</SelectItem>
                  <SelectItem value="all">All (incl. closed)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Customer Search</Label>
              <Input
                placeholder="Filter by name…"
                value={customerSearch}
                onChange={e => setCustomerSearch(e.target.value)}
                className="mt-1"
                data-testid="filter-customer"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Min WIP $</Label>
              <Input
                type="number"
                min="0"
                value={minWip}
                onChange={e => setMinWip(e.target.value)}
                className="mt-1"
                data-testid="filter-min-wip"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rollup */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="loading-state">Loading…</div>
      ) : error ? (
        <Card className="border-red-200 bg-red-50 dark:bg-red-950/20" data-testid="error-state">
          <CardContent className="pt-6 text-sm text-red-700">{error}</CardContent>
        </Card>
      ) : visibleCustomers.length === 0 ? (
        <Card data-testid="empty-state">
          <CardContent className="pt-12 pb-12 text-center">
            <Package className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
            <h3 className="font-semibold">No open dry-hire ledgers</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {customerSearch ? 'No customers match the search.' :
                statusFilter === 'overdue_only' ? 'Nothing is overdue — nice work.' :
                  'Once a dry-hire job is booked or on-hire, it will appear here with its WIP $.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <div>
              <CardTitle className="text-lg">Customer Rollup</CardTitle>
              <CardDescription>
                Sorted by WIP $ (highest first). Click a row to drill down by hire.
              </CardDescription>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={expandAll} data-testid="expand-all-btn">Expand all</Button>
              <Button variant="ghost" size="sm" onClick={collapseAll} data-testid="collapse-all-btn">Collapse all</Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Open Hires</TableHead>
                  <TableHead className="text-right">WIP $</TableHead>
                  <TableHead className="text-right">If Off-Hired Today</TableHead>
                  <TableHead className="text-right">Billed-to-Date</TableHead>
                  <TableHead>Oldest Hire</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleCustomers.map((c) => {
                  const cid = c.customer_id || '__none__';
                  const isOpen = !!expanded[cid];
                  return (
                    <CustomerRow
                      key={cid}
                      cid={cid}
                      customer={c}
                      isOpen={isOpen}
                      onToggle={() => toggleExpand(cid)}
                      onDrillDown={(hire) => setDrillDown({
                        jobId: hire.job_id,
                        jobNumber: hire.job_number,
                        customerName: c.customer_name,
                        assetLabel: hire.asset_label,
                      })}
                      currency={currency}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Drill-down sheet — reuses the existing per-job invoicing panel */}
      <Sheet open={!!drillDown} onOpenChange={(o) => !o && setDrillDown(null)}>
        <SheetContent className="sm:max-w-2xl w-full overflow-y-auto" data-testid="drill-down-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {drillDown ? `Invoice Job #${drillDown.jobNumber} — ${drillDown.assetLabel || ''}` : ''}
            </SheetTitle>
            {drillDown?.customerName && (
              <p className="text-sm text-muted-foreground">{drillDown.customerName}</p>
            )}
          </SheetHeader>
          {drillDown && (
            <div className="mt-4">
              <DryHireInvoicingPanel api={api} jobId={drillDown.jobId} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ---------- Subcomponents (kept inline to avoid file sprawl) ---------------

function KpiCard({ icon: Icon, label, value, accent = '', testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
          <Icon className="h-3.5 w-3.5" />
          <span>{label}</span>
        </div>
        <div className={`mt-2 text-2xl font-bold tabular-nums ${accent}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function CustomerRow({ cid, customer, isOpen, onToggle, onDrillDown, currency }) {
  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/40"
        onClick={onToggle}
        data-testid={`customer-row-${cid}`}
      >
        <TableCell>
          {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </TableCell>
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            {customer.customer_name}
            {customer.any_overdue && (
              <Badge variant="destructive" className="text-[10px]">Overdue</Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-right tabular-nums">{customer.open_hires}</TableCell>
        <TableCell className="text-right tabular-nums font-semibold text-emerald-600">{fmt$(customer.wip_total, currency)}</TableCell>
        <TableCell className="text-right tabular-nums text-muted-foreground">{fmt$(customer.wip_blend_total, currency)}</TableCell>
        <TableCell className="text-right tabular-nums">{fmt$(customer.billed_to_date, currency)}</TableCell>
        <TableCell className="text-xs text-muted-foreground">{customer.oldest_on_hire_date || '—'}</TableCell>
        <TableCell></TableCell>
      </TableRow>

      {isOpen && customer.hires.map(h => (
        <TableRow key={h.job_id} className="bg-muted/30 hover:bg-muted/50" data-testid={`hire-row-${h.job_id}`}>
          <TableCell></TableCell>
          <TableCell colSpan={6}>
            <div className="flex items-center justify-between gap-3 py-1">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">#{h.job_number}</span>
                  <span className="text-sm">{h.asset_label || '—'}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-medium ${STATUS_COLOURS[h.hire_status] || ''}`}>
                    {(h.hire_status || '').replace('_', ' ')}
                  </span>
                  {h.is_overdue && <Badge variant="destructive" className="text-[10px]">Overdue</Badge>}
                  {h.missing_rates && (
                    <span className="text-[10px] text-amber-700 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 rounded">
                      No rate
                    </span>
                  )}
                  {h.period_unit && (
                    <span className="text-[10px] text-muted-foreground uppercase">· {h.period_unit === 'day' ? 'Daily' : h.period_unit + 'ly'}</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-3">
                  {h.site_label && <span>{h.site_label}</span>}
                  <span>Started {h.start_date}</span>
                  {h.last_invoiced_to ? (
                    <span>Last invoiced through {h.last_invoiced_to}</span>
                  ) : (
                    <span>Never invoiced</span>
                  )}
                  {h.days_on_hire !== null && <span>{h.days_on_hire}d on hire</span>}
                  {h.days_uninvoiced > 0 && <span>{h.days_uninvoiced}d uninvoiced</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold tabular-nums text-emerald-600">{fmt$(h.wip_full_cycles, currency)}</div>
                <div className="text-[11px] text-muted-foreground tabular-nums">if off-hired: {fmt$(h.wip_blend, currency)}</div>
              </div>
              <div className="text-right shrink-0 hidden md:block min-w-[90px]">
                <div className="text-sm tabular-nums">{fmt$(h.billed_to_date, currency)}</div>
                <div className="text-[11px] text-muted-foreground">billed</div>
              </div>
            </div>
          </TableCell>
          <TableCell className="text-right">
            <Button
              size="sm"
              variant="default"
              onClick={(e) => { e.stopPropagation(); onDrillDown(h); }}
              data-testid={`invoice-now-btn-${h.job_id}`}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Invoice
            </Button>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}
